import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from '../../../../config/axios';
import UserContext from '../../../../contexts/UserContext';
import {
  InputDiv,
  Label,
  Input,
  SearchOptions,
  Option,
  VectorIcon,
} from '../Select/select-style';
import vector from '../../../../assets/equipment/vector.svg';
import ReactDOM from 'react-dom';

interface ItemType {
  id: number;
  [key: string]: any;
}

interface UserSelectProps<T extends ItemType> {
  label: string;
  apiUrl: string;
  displayField: keyof T;
  value: T | null;
  onChange: (item: T | null) => void;
  profileId?: number;
  profileName?: string;
  permission?: string;
}

const UserSelect = <T extends ItemType>({
  label,
  apiUrl,
  displayField,
  value,
  onChange,
  profileId,
  profileName,
  permission,
}: UserSelectProps<T>) => {
  const { user } = useContext(UserContext);
  const [items, setItems] = useState<T[]>([]);
  const [allSellers, setAllSellers] = useState<T[]>([]);
  const [inputItem, setInputItem] = useState<string>(
    (value?.[displayField] as string) || '',
  );
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    setInputItem((value?.[displayField] as string) || '');
  }, [value, displayField]);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const token = localStorage.getItem('token');

        const params: any = { loadQuantity: 1000 };
        if (permission) {
          params.permission = permission;
        }

        const response = await axios.get<T[]>(apiUrl, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        let responseData: T[] = [];
        if (Array.isArray(response.data)) {
          responseData = response.data;
        } else if (response.data && Array.isArray((response.data as any).data)) {
          responseData = (response.data as any).data;
        } else {
          console.error('Invalid API response, expected an array.');
          setAllSellers([]);
          setItems([]);
          return;
        }

        let filteredItems = responseData;

        if (profileId !== undefined) {
          filteredItems = responseData.filter(
            (item) => (item as any).profileId === profileId,
          );
        } else if (profileName !== undefined) {
          filteredItems = responseData.filter(
            (item) => (item as any).profile?.name === profileName,
          );
        }

        setAllSellers(filteredItems);
        setItems(filteredItems);
      } catch (error) {
        console.error('Error fetching data:', error);
        setAllSellers([]);
        setItems([]);
      }
    };
    fetchSellers();
  }, [apiUrl, profileId, profileName, permission]);

  useEffect(() => {
    if (showOptions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showOptions, inputRef]);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputItem(newValue);
    setSelectedIndex(-1);

    const filterCriteria = newValue.toLowerCase();
    const filteredItems = allSellers.filter((item) =>
      (item[displayField] as string)?.toLowerCase()?.includes(filterCriteria),
    );
    setItems(filteredItems);

    setShowOptions(true);
    if (newValue === '') {
      onChange(null);
    }
  };

  const handleSelectItem = (item: T) => {
    setInputItem(item[displayField] as string);
    setShowOptions(false);
    onChange(item);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex !== -1 && items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      } else {
        setShowOptions(false);
        onChange(null);
      }
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      const selectedItem = items.find(
        (item) =>
          (item[displayField] as string)?.toLowerCase() ===
          inputItem.toLowerCase(),
      );

      if (!selectedItem) {
        setInputItem('');
        onChange(null);
      }
      setShowOptions(false);
    }, 150);
  };

  return (
    <InputDiv>
      <Label>{label}</Label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Input
          ref={inputRef}
          type='text'
          placeholder='Selecione'
          value={inputItem}
          onFocus={() => setShowOptions(true)}
          onChange={handleChangeInput}
          onKeyDown={handleSetOptionByKey}
          onBlur={handleInputBlur}
        />
        <VectorIcon src={vector} alt="dropdown arrow" />
      </div>
      {showOptions &&
        items.length > 0 &&
        ReactDOM.createPortal(
          <SearchOptions
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {items.map((s, index) => (
              <Option
                key={s.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelectItem(s)}
                isSelected={index === selectedIndex}
              >
                {s[displayField] as React.ReactNode}
              </Option>
            ))}
          </SearchOptions>,
          document.body,
        )}
    </InputDiv>
  );
};

export default UserSelect;
